import React, { useState } from 'react';
import { Button, Divider, Modal, Space, Tooltip, message } from 'antd';
import { DollarCircleOutlined, PlusOutlined, LinkOutlined } from '@ant-design/icons';
import { TextService } from '../../../Services/TextService';
import { MemberService } from '../../../Services/MemberService';
import { IntegrationService } from '../../../Services/IntegrationService';
import { WebApiMemberDtoExternalPaymentDto, WebApiMemberDtoMemberDto, WebApiMemberDtoPaymentDto, WebApiModelsMembershipProduct } from '../../../Repository/eventbookyapi';
import { IntegrationType } from '../../../Types/IntegrationType';
import { ExternalPaymentStatus } from '../../../Types/ExternalPaymentStatus';
import { downloadBlobFile, getFileNameFromContentDisposition } from '../../../Helpers/FileDownloadHelper';
import PaymentTable, { UnifiedPayment } from './PaymentTable';
import RefundModal from './RefundModal';
import AddManualPayment from './AddManualPayment';
import AddPaymentLinkForm from './AddPaymentLinkForm';
import { useAuth } from '../../../Contexts/AuthContext';
import { getCurrentYearAsString } from '../../../Helpers/DateHelpers';

interface ManagePaymentsProps {
    member: WebApiMemberDtoMemberDto;
    membershipProducts?: WebApiModelsMembershipProduct[];
    loadMembers: any;
    hasPingPaymentIntegration?: boolean;
    hasFortnoxIntegration?: boolean;
    hasYearlyPayments?: boolean;
}

const ManagePayments = (props: ManagePaymentsProps) => {
    const GetText = TextService.GetText;
    const [open, setOpen] = useState(false);
    const [payments, setPayments] = useState<WebApiMemberDtoPaymentDto[]>([]);
    const [externalPayments, setExternalPayments] = useState<WebApiMemberDtoExternalPaymentDto[]>([]);
    const [receiptLoadingId, setReceiptLoadingId] = useState<string | null>(null);
    const [invoiceLoadingId, setInvoiceLoadingId] = useState<string | null>(null);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [refundPayment, setRefundPayment] = useState<WebApiMemberDtoExternalPaymentDto | null>(null);
    const [showManualForm, setShowManualForm] = useState(false);
    const [showPaymentLinkForm, setShowPaymentLinkForm] = useState(false);
    const { user } = useAuth();

    const onCancel = () => {
        setOpen(false);
        setShowManualForm(false);
        setShowPaymentLinkForm(false);
        if (props.loadMembers) {
            props.loadMembers.length > 0 ? props.loadMembers(false) : (props.loadMembers as () => void)();
        }
    };

    const loadPayments = () => {
        if (!props.member.memberId) return;

        MemberService.GetPayments(props.member.memberId).then(result => {
            setPayments(result.manualPayments || []);
            setExternalPayments(result.externalPayments || []);
        });
    };

    const mapExternalStatus = (status?: number): UnifiedPayment['status'] => {
        switch (status) {
            case ExternalPaymentStatus.Paid: return 'paid';
            case ExternalPaymentStatus.Refunded: return 'refunded';
            case ExternalPaymentStatus.Settled: return 'settled';
            case ExternalPaymentStatus.Pending:
            default: return 'pending';
        }
    };

    const buildUnifiedPayments = (): UnifiedPayment[] => {
        const unified: UnifiedPayment[] = [];

        // Add manual payments
        payments.forEach(p => {
            unified.push({
                key: `manual-${p.id}`,
                type: 'manual',
                paymentDate: p.paymentDate,
                amount: p.amount,
                year: p.year,
                status: 'paid',
                original: p,
                manualPaymentId: p.id!,
                createdByName: p.createdByName,
            });
        });

        // Add external payments
        externalPayments.forEach(ep => {
            let type: UnifiedPayment['type'];
            if (ep.integrationType === IntegrationType.Fortnox) {
                type = 'fortnox';
            } else if (ep.integrationType === IntegrationType.Spiris) {
                type = 'spiris';
            } else {
                type = 'pingpayments';
            }
            unified.push({
                key: `external-${ep.id}`,
                type,
                paymentDate: ep.payDate,
                amount: ep.totalAmount,
                year: ep.connectedYear,
                status: mapExternalStatus(ep.status),
                original: ep,
                paymentLinkId: ep.paymentLinkId,
                payUrl: ep.payUrl,
                invoiceUrl: ep.invoiceUrl,
                dueDate: ep.dueDate,
                invoiceDate: ep.invoiceDate,
                settledAt: ep.settledAt,
                createdAt: ep.createdAt ? new Date(ep.createdAt) : undefined,
                createdByName: ep.createdByName,
                refundDescription: ep.refund?.description,
            });
        });

        // Sort by date descending (most recent first)
        unified.sort((a, b) => {
            const dateA = a.createdAt ? a.createdAt.getTime() : a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
            const dateB = b.createdAt ? b.createdAt.getTime() : b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
            return dateB - dateA;
        });

        return unified;
    };

    // --- Action handlers ---

    const handleDeleteManual = (id: number) => {
        MemberService.DeletePayment(id).then(() => loadPayments());
    };

    const handleDeletePaymentLink = async (payUrl: string) => {
        try {
            await IntegrationService.CancelPaymentLink(payUrl);
            loadPayments();
            message.success(GetText("AddPaymentLink_LinkDeleted"));
        } catch {
            message.error(GetText("EditMember_ErrorSaving"));
        }
    };

    const handleDownloadReceipt = async (paymentLinkId: string) => {
        try {
            setReceiptLoadingId(paymentLinkId);
            const result = await IntegrationService.DownloadReceiptFromPaymentLink(paymentLinkId);
            const fileName = getFileNameFromContentDisposition(result.contentDisposition, `receipt-${paymentLinkId}.pdf`);
            downloadBlobFile(result.blob, fileName);
        } catch {
            message.error(GetText("AddPaymentLink_ReceiptError"));
        } finally {
            setReceiptLoadingId(null);
        }
    };

    const handleGenerateAndDownloadInvoice = async (paymentLinkId: string) => {
        try {
            setInvoiceLoadingId(paymentLinkId);
            // Always create a fresh invoice first (PingPayments invoices expire after 1 hour)
            await IntegrationService.CreateInvoiceFromPaymentLink(paymentLinkId);
            // Then immediately download it
            const result = await IntegrationService.DownloadInvoiceFromPaymentLink(paymentLinkId);
            const fileName = getFileNameFromContentDisposition(result.contentDisposition, `invoice-${paymentLinkId}.pdf`);
            downloadBlobFile(result.blob, fileName);
        } catch {
            message.error(GetText("AddPaymentLink_InvoiceCreationError"));
        } finally {
            setInvoiceLoadingId(null);
        }
    };

    const handleRefund = (payment: WebApiMemberDtoExternalPaymentDto) => {
        setRefundPayment(payment);
        setRefundModalOpen(true);
    };

    const handleRefunded = () => {
        setRefundModalOpen(false);
        setRefundPayment(null);
        loadPayments();
    };

    const handlePaymentSaved = () => {
        setShowManualForm(false);
        loadPayments();
    };

    const handleLinkCreated = () => {
        setShowPaymentLinkForm(false);
        loadPayments();
    };

    // --- Membership products info ---
    const renderMembershipInfo = () => {
        if (!props.member.membershipProduct || props.member.membershipProduct === 0) return null;

        const mainProduct = props.membershipProducts?.find(p => p.id === props.member.membershipProduct);
        const addOnProducts = props.member.membershipProductAddOn?.map(id =>
            props.membershipProducts?.find(p => p.id === id)
        ).filter(Boolean);

        return (
            <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                <b>{GetText("MemberDetails_MembershipInfo")}</b>
                <div style={{ marginTop: '4px' }}>
                    {mainProduct?.name && (
                        <div>{mainProduct.name}: {mainProduct.price} kr</div>
                    )}
                    {addOnProducts?.map((product) => (
                        <div key={product!.id}>{product!.name}: {product!.price} kr</div>
                    ))}
                </div>
            </div>
        );
    };

    const unifiedPayments = buildUnifiedPayments();
    const hasAnyIntegration = props.hasPingPaymentIntegration || props.hasFortnoxIntegration;

    return (
        <>
            <Modal
                open={open}
                title={GetText("ManagePayments_ModalTitle") + " - (" + props.member.uniqueId + ") " + props.member.firstName + " " + props.member.lastName}
                width={800}
                okText={GetText("EditMember_ModalClose")}
                onOk={onCancel}
                onCancel={onCancel}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                {/* Membership products info */}
                {renderMembershipInfo()}

                {/* Unified payment table */}
                <PaymentTable
                    payments={unifiedPayments}
                    onDeleteManual={handleDeleteManual}
                    onDeletePaymentLink={handleDeletePaymentLink}
                    onDownloadReceipt={handleDownloadReceipt}
                    onGenerateAndDownloadInvoice={handleGenerateAndDownloadInvoice}
                    onRefund={handleRefund}
                    receiptLoadingId={receiptLoadingId}
                    invoiceLoadingId={invoiceLoadingId}
                />

                {/* Action buttons at the bottom */}
                <Divider />
                {hasAnyIntegration ? (
                    <>
                        {!showManualForm && !showPaymentLinkForm && (
                            <Space>
                                <Button
                                    disabled={user?.hasOnlyReadRights}
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={() => setShowManualForm(true)}
                                >
                                    {GetText("ManagePayments_AddManualButton")}
                                </Button>
                                <Button
                                    type="dashed"
                                    icon={<LinkOutlined />}
                                    onClick={() => setShowPaymentLinkForm(true)}
                                >
                                    {GetText("ManagePayments_AddPaymentLinkButton")}
                                </Button>
                            </Space>
                        )}

                        {/* Manual payment form (collapsible) */}
                        {showManualForm && (
                            <AddManualPayment
                                memberId={props.member.memberId!}
                                onPaymentSaved={handlePaymentSaved}
                                onCancel={() => setShowManualForm(false)}
                            />
                        )}

                        {/* Payment link form */}
                        {showPaymentLinkForm && (
                            <AddPaymentLinkForm
                                externalPayments={externalPayments}
                                memberId={props.member.memberId!}
                                onLinkCreated={handleLinkCreated}
                                onCancel={() => setShowPaymentLinkForm(false)}
                            />
                        )}
                    </>
                ) : (
                    /* No integrations — manual form always visible */
                    <AddManualPayment
                        memberId={props.member.memberId!}
                        onPaymentSaved={handlePaymentSaved}
                    />
                )}
            </Modal>

            {/* Refund modal */}
            <RefundModal
                payment={refundPayment}
                open={refundModalOpen}
                onClose={() => { setRefundModalOpen(false); setRefundPayment(null); }}
                onRefunded={handleRefunded}
            />

            {/* Trigger icons */}
            <Space size="middle">
                {props.hasYearlyPayments && (
                    <DollarCircleOutlined
                        className={props.member.isPaidCurrentYear ? "" : "not-payed"}
                        style={{ fontSize: "20px" }}
                        type="primary"
                        onClick={() => {
                            setOpen(true);
                            loadPayments();
                        }}
                    />
                )}
                {props.hasPingPaymentIntegration && (
                    <LinkOutlined
                        style={{
                            fontSize: "20px",
                            color: props.member.paymentLink ? undefined : "#d9d9d9",
                            cursor: "pointer",
                        }}
                        onClick={() => {
                            setOpen(true);
                            loadPayments();
                        }}
                    />
                )}
            </Space>
        </>
    );
};

export default ManagePayments;
